import React, { useContext, useId, useRef } from 'react';

import classnames from 'classnames';
import { useCarouselChildrenMap } from './use-carousel-children-map';

import type { CarouselChildrenMap } from './use-carousel-children-map';

type CarouselContextType = Pick<
    CarouselProps,
    'padX' | 'numberItemsToShow' | 'numberItemsToScroll' | 'showScrollbar'
> &
    Pick<
        CarouselChildrenMap,
        | 'childrenMap'
        | 'updateChildrenMap'
        | 'isFirstItemVisible'
        | 'isLastItemVisible'
        | 'areAllItemsHidden'
        | 'firstVisibleItemIndex'
        | 'lastVisibleItemIndex'
        | 'totalCount'
    > & {
        rows: NonNullable<CarouselProps['rows']>; // we know this is atleast 1 but the props say it's optional (e.g. null/undef)
        gap: NonNullable<CarouselProps['gap']>; // we know this is not null/undef but the props say it's optional (e.g. null/undef)
        contentWrapperRef: React.RefObject<HTMLDivElement>;
        uniqueId: React.MutableRefObject<string>; // FIXME, should likely just be string here
    };

const CarouselContext = React.createContext<CarouselContextType | undefined>(
    undefined
);

export const useCarouselContext = () => {
    const context = useContext(CarouselContext);

    if (!context) {
        throw new Error(
            'CarouselContent cannot be used outside of CarouselContext'
        );
    }
    return context;
};

const getAvailableWidthPerItem = (numberItemsToShow = 1, gap: string) => {
    const numberOfGaps = numberItemsToShow - 1;
    const gapWidth = `(${numberOfGaps} * ${gap}) / ${numberItemsToShow}`;
    const itemWidthPercentage = (1 / numberItemsToShow) * 100;
    return `calc(${itemWidthPercentage}% - ${gapWidth})`;
};

type CarouselProps = {
    children: React.ReactNode;
    /**
     * If rows > 1 this treats one "column" as one "item", i.e numberItemsToScroll="3"
     * will scroll three entire columns at the time, not just three items (which would
     * be one column in this example).
     */
    numberItemsToScroll: number;
    /**
     * If not specified the items will be of flexible width
     */
    numberItemsToShow?: number;
    /**
     * Defaults to false
     */
    showScrollbar?: boolean;
    /**
     * Only works when used together with numberItemsToShow
     */
    rows?: number;
    /**
     * Gap width as CSS string, for example "1rem" or "14px", to specify different column
     * and row gap use "1rem 2rem"
     */
    gap?: string;
    /**
     * Optional id to set. If not provided a random ID will be generated
     */
    id?: string;
    /**
     * If first and last items should be padded with same gap as between items
     */
    padX?: boolean;
    className?: string;
    ariaLabel?: string;
};

/**
 * Carousel
 *
 * A simple carousel that uses buttons to scroll on desktop and buttons + touch to scroll on tablet and down.
 *
 * Typical usage:
 * <Carousel {...args} className="mx-3">
 *   <LeftButton />
 *   <CarouselContent>
 *       {buttons.map((_, index) => (
 *           <Button
 *               theme="action"
 *               outline={selectedIndex === index ? 'filled' : 'outline'}
 *               key={index}
 *               onClick={() => setSelectedIndex(index)}>
 *               Option {index} {index % 3 === 0 ? 'extra long' : ''}
 *           </Button>
 *       ))}
 *   </CarouselContent>
 *   <RightButton />
 * </Carousel>
 *
 * Thanks https://dev.to/rakumairu/simple-react-carousel-24m0
 * Thanks https://dev.to/wolffe/i-created-a-draggable-carousel-with-momentum-scrolling-and-mobile-support-using-vanilla-javascript-17he
 */
export const Carousel = ({
    children,
    className,
    gap = '1rem',
    numberItemsToShow,
    numberItemsToScroll,
    showScrollbar,
    rows = 1,
    id,
    padX,
    ariaLabel
}: CarouselProps) => {
    const generatedId = useId();
    const uniqueId = useRef(id ?? `carousel-${generatedId}`);
    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const {
        childrenMap,
        updateChildrenMap,
        isFirstItemVisible,
        isLastItemVisible,
        areAllItemsHidden,
        lastVisibleItemIndex,
        firstVisibleItemIndex,
        totalCount
    } = useCarouselChildrenMap();
    const availableWidthPerItem = getAvailableWidthPerItem(
        numberItemsToShow,
        gap
    );
    if (!numberItemsToScroll) {
        throw new Error('Carousel: numberItemsToScroll prop must be provided');
    }

    return (
        <CarouselContext.Provider
            value={{
                gap,
                numberItemsToShow,
                numberItemsToScroll,
                childrenMap,
                updateChildrenMap,
                contentWrapperRef,
                isFirstItemVisible,
                isLastItemVisible,
                areAllItemsHidden,
                firstVisibleItemIndex,
                lastVisibleItemIndex,
                showScrollbar,
                rows,
                padX,
                totalCount,
                // for aria purposes
                uniqueId
            }}>
            <div
                className={classnames(className, 'position-relative')}
                style={
                    {
                        '--available-width-per-item': availableWidthPerItem
                    } as React.CSSProperties
                }
                aria-roledescription="carousel"
                aria-label={ariaLabel}>
                {children}
            </div>
        </CarouselContext.Provider>
    );
};
